
import { useState, useRef, useEffect, useCallback } from 'react';
import { GalleryItem, DJProfile } from '../../../lib/supabase';
import GalleryLightbox from './GalleryLightbox';

interface GalleryProps {
  items: GalleryItem[];
  profile?: DJProfile | null;
}

export default function Gallery({ items, profile }: GalleryProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [filter, setFilter] = useState<'all' | 'photo' | 'video'>('all');
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);

  const sectionSubtitle = profile?.section_gallery_subtitle || 'Galería';
  const sectionTitle = profile?.section_gallery_title || 'Momentos\nÚnicos';
  const sectionDescription = profile?.section_gallery_description || 'Fotos y vídeos de las sesiones y eventos más especiales.';

  const filtered = filter === 'all' ? items : items.filter(i => i.type === filter);

  const openLightbox = (index: number) => {
    if (!isDragging) setLightboxIndex(index);
  };
  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = () => setLightboxIndex(prev => (prev !== null && prev > 0 ? prev - 1 : prev));
  const goNext = () => setLightboxIndex(prev => (prev !== null && prev < filtered.length - 1 ? prev + 1 : prev));

  const startAutoScroll = useCallback(() => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    autoScrollRef.current = setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft >= maxScroll - 2) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: 220, behavior: 'smooth' });
      }
    }, 3000);
  }, []);

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  };

  useEffect(() => {
    startAutoScroll();
    return () => stopAutoScroll();
  }, [filtered, startAutoScroll]);

  const handleTouchStart = () => stopAutoScroll();
  const handleTouchEnd = () => {
    setTimeout(() => startAutoScroll(), 4000);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(false);
    dragStartX.current = e.clientX;
    scrollStartX.current = scrollRef.current?.scrollLeft || 0;
    stopAutoScroll();

    const handleMouseMove = (ev: MouseEvent) => {
      const diff = dragStartX.current - ev.clientX;
      if (Math.abs(diff) > 5) setIsDragging(true);
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollStartX.current + diff;
      }
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setTimeout(() => {
        setIsDragging(false);
        startAutoScroll();
      }, 100);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  if (items.length === 0) return null;

  return (
    <section id="gallery" className="py-24 lg:py-32 bg-black relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-fuchsia-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="text-fuchsia-400 text-xs uppercase tracking-[0.3em] font-light">{sectionSubtitle}</span>
            <h2 className="text-5xl lg:text-6xl font-black text-white mt-4 leading-tight">
              {sectionTitle.split('\n').map((line, i, arr) => (
                <span key={i}>
                  {i === arr.length - 1 ? (
                    <span className="bg-gradient-to-r from-cyan-400 to-fuchsia-400 bg-clip-text text-transparent">{line}</span>
                  ) : (
                    <>{line}<br /></>
                  )}
                </span>
              ))}
            </h2>
            <p className="text-gray-400 text-lg mt-4 leading-relaxed max-w-xl">
              {sectionDescription}
            </p>
          </div>

          <div className="flex items-center gap-1 bg-[#111] border border-gray-800 rounded-full p-1 w-fit">
            {(['all', 'photo', 'video'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  filter === f
                    ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white shadow-[0_0_15px_rgba(6,182,212,0.3)]'
                    : 'text-gray-500 hover:text-white'
                }`}
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`text-xs ${f === 'all' ? 'ri-grid-line' : f === 'photo' ? 'ri-image-line' : 'ri-play-circle-line'}`}></i>
                </div>
                {f === 'all' ? 'Todo' : f === 'photo' ? 'Fotos' : 'Vídeos'}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-gray-800 rounded-2xl">
            <p className="text-gray-600 text-sm">No hay elementos en esta categoría.</p>
          </div>
        ) : (
          <>
            {/* Mobile: horizontal scroll carousel */}
            <div
              ref={scrollRef}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onMouseDown={handleMouseDown}
              className="md:hidden flex gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4 -mx-6 px-6 select-none"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            >
              {filtered.map((item, index) => (
                <div
                  key={item.id}
                  className="flex-shrink-0 w-64 snap-start"
                >
                  <MobileGalleryCard item={item} onClick={() => openLightbox(index)} />
                </div>
              ))}
            </div>

            {/* Desktop: masonry grid */}
            <div className="hidden md:block columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
              {filtered.map((item, index) => (
                <GalleryCard
                  key={item.id}
                  item={item}
                  onClick={() => openLightbox(index)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {lightboxIndex !== null && (
        <GalleryLightbox
          item={filtered[lightboxIndex]}
          onClose={closeLightbox}
          onPrev={goPrev}
          onNext={goNext}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex < filtered.length - 1}
        />
      )}
    </section>
  );
}

function MobileGalleryCard({ item, onClick }: { item: GalleryItem; onClick: () => void }) {
  const thumbnail = item.thumbnail_url || item.url;

  return (
    <div
      onClick={onClick}
      className="relative rounded-xl overflow-hidden cursor-pointer border border-gray-800/50"
    >
      <div className="relative w-64 h-80">
        <img
          src={thumbnail}
          alt={item.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
        {item.type === 'video' && (
          <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-fuchsia-500/90 rounded-full shadow-lg">
            <i className="ri-play-fill text-white text-sm"></i>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-sm font-semibold leading-tight">{item.title}</p>
          <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${
            item.type === 'video' ? 'bg-fuchsia-500/30 text-fuchsia-300' : 'bg-cyan-500/30 text-cyan-300'
          }`}>
            {item.type === 'video' ? 'Vídeo' : 'Foto'}
          </span>
        </div>
      </div>
    </div>
  );
}

function GalleryCard({ item, onClick }: { item: GalleryItem; onClick: () => void }) {
  const thumbnail = item.thumbnail_url || item.url;

  return (
    <div
      onClick={onClick}
      className="group relative break-inside-avoid rounded-xl overflow-hidden cursor-pointer border border-gray-800/50 hover:border-cyan-400/40 transition-all duration-300 hover:shadow-[0_0_25px_rgba(6,182,212,0.15)]"
    >
      <div className="relative w-full">
        <img
          src={thumbnail}
          alt={item.title}
          className="w-full h-auto object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        {item.type === 'video' && (
          <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-fuchsia-500/90 rounded-full shadow-lg">
            <i className="ri-play-fill text-white text-sm"></i>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-14 h-14 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
            <i className={`text-white text-2xl ${item.type === 'video' ? 'ri-play-fill' : 'ri-zoom-in-line'}`}></i>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <p className="text-white text-sm font-semibold leading-tight">{item.title}</p>
          <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${
            item.type === 'video' ? 'bg-fuchsia-500/30 text-fuchsia-300' : 'bg-cyan-500/30 text-cyan-300'
          }`}>
            {item.type === 'video' ? 'Vídeo' : 'Foto'}
          </span>
        </div>
      </div>
    </div>
  );
}
